// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Staff & roles - Govern360 employees with admin access.
// G360_K482 - both lists are the shared DataTable (sort, filter, column
// picker, CSV export); role changes and removal stay in the row.
import { useEffect, useState } from 'react';
import { Users, UserPlus } from 'lucide-react';
import {
  listStaff, setStaffRole, removeStaff,
  inviteStaff, listPendingStaff, cancelStaffInvitation,
  STAFF_ROLE_LABEL, STAFF_ROLES,
} from '../lib/adminApi.js';
import { EmptyState, Field } from '@govern360/shared/components/ui.jsx';
import Drawer from '@govern360/shared/components/Drawer.jsx';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('support');
  const [dept, setDept] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  async function reload() {
    const [{ data: s, error: se }, { data: p, error: pe }] = await Promise.all([listStaff(), listPendingStaff()]);
    const bad = se || pe;
    setLoadErr(bad ? ((se ? 'staff' : 'pending invitations') + ': ' + (bad.message || 'the read failed')) : '');
    setStaff(s);
    setPending(p);
    setLoading(false);
  }
  useEffect(() => { reload(); }, []);

  async function onRole(userId, r) {
    setBusy(userId);
    const { error } = await setStaffRole(userId, r);
    setBusy(null);
    if (error) { alert(error.message || 'Could not change role'); return; }
    reload();
  }
  async function onRemove(userId, label) {
    if (!window.confirm('Remove ' + label + ' from staff? They will lose admin access.')) return;
    setBusy(userId);
    const { error } = await removeStaff(userId);
    setBusy(null);
    if (error) { alert(error.message || 'Could not remove staff member'); return; }
    reload();
  }
  async function onInvite() {
    if (!email.trim()) { alert('Enter an email address.'); return; }
    setSaving(true);
    const { error } = await inviteStaff(email.trim(), role, dept.trim());
    setSaving(false);
    if (error) { alert(error.message || 'Could not send invite'); return; }
    setOpen(false); setEmail(''); setRole('support'); setDept('');
    reload();
  }
  async function onCancelInvite(id, label) {
    if (!window.confirm('Cancel the invite for ' + label + '?')) return;
    const { error } = await cancelStaffInvitation(id);
    if (error) { alert(error.message || 'Could not cancel invite'); return; }
    reload();
  }

  const day = (v) => { const ms = Date.parse(v || ''); return isNaN(ms) ? null : ms; };
  const nameOf = (s) => (s.profile && s.profile.full_name) || s.email || 'Unknown';
  const pendingCols = [
    { key: 'email', label: 'Email', value: (p) => p.email || '', render: (p) => <strong>{p.email}</strong> },
    { key: 'role', label: 'Role', value: (p) => STAFF_ROLE_LABEL[p.role] || p.role || '', render: (p) => <span className="pill p-gray">{STAFF_ROLE_LABEL[p.role] || p.role}</span> },
    { key: 'dept', label: 'Department', value: (p) => p.department || '' },
    { key: 'invited', label: 'Invited', value: (p) => String(p.invited_at || '').slice(0, 10), sort: (p) => day(p.invited_at),
      render: (p) => (day(p.invited_at) == null ? null : <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{new Date(p.invited_at).toLocaleDateString()}</span>) },
  ];
  const staffCols = [
    { key: 'name', label: 'Name', value: nameOf,
      render: (s) => (<span><span className="mini-av admin" style={{ background: 'var(--g-admin)' }}>{nameOf(s).split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase()}</span><strong>{nameOf(s)}</strong></span>) },
    { key: 'email', label: 'Email', value: (s) => s.email || '' },
    { key: 'role', label: 'Role', value: (s) => STAFF_ROLE_LABEL[s.role] || s.role || '',
      render: (s) => <span className={`pill ${s.role === 'super_admin' ? 'p-warn' : 'p-gray'}`}>{STAFF_ROLE_LABEL[s.role] || s.role}</span> },
    { key: 'dept', label: 'Department', value: (s) => s.department || '' },
    { key: 'since', label: 'Since', value: (s) => String(s.created_at || '').slice(0, 10), sort: (s) => day(s.created_at),
      render: (s) => (day(s.created_at) == null ? null : <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{new Date(s.created_at).toLocaleDateString()}</span>) },
  ];

  return (
    <div>
      <div className="ph">
        <div><h1 className="ph-h1">Staff &amp; roles</h1><p className="ph-sub">Govern360 employees with access to this admin console.</p></div>
        <div className="ph-cta">
          <button className="btn admin-primary" onClick={() => setOpen(true)}><UserPlus size={16} />Invite staff</button>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading...</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>The staff list could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="card pad-0" style={{ marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--g-divider)', fontWeight: 500, fontSize: 13 }}>
                Pending invites <span style={{ color: 'var(--g-text-2)', fontWeight: 400 }}>- they become staff automatically on first sign-in</span>
              </div>
              <div style={{ padding: '10px 12px' }}>
                <DataTable rows={pending} columns={pendingCols} card={false} tableClass="g360-admin-pend-tb"
                  storageKey="g360.admin.pending.hiddenCols" name="pending-staff-invites" placeholder="Filter invites..."
                  rowKey={(p, i) => p.id || i}
                  actions={(p) => <button className="btn outline" style={{ height: 30, padding: '0 12px' }} onClick={() => onCancelInvite(p.id, p.email)}>Cancel</button>} />
              </div>
            </div>
          )}

          {staff.length === 0 ? (
            <EmptyState icon={Users} title="No staff yet" hint="Invite a teammate above - they get access automatically the first time they sign in." />
          ) : (
            <DataTable rows={staff} columns={staffCols} tableClass="g360-admin-staff-tb"
              storageKey="g360.admin.staff.hiddenCols" name="staff" placeholder="Filter staff..."
              rowKey={(s, i) => s.user_id || i} defaultHidden={['email']}
              actions={(s) => (
                <span>
                  <select className="inp" value={s.role} disabled={busy === s.user_id}
                    onChange={(e) => onRole(s.user_id, e.target.value)}
                    style={{ height: 32, width: 'auto', display: 'inline-block', padding: '0 8px', marginRight: 8 }}>
                    {STAFF_ROLES.map((r) => (<option key={r} value={r}>{STAFF_ROLE_LABEL[r] || r}</option>))}
                  </select>
                  <button className="btn outline" disabled={busy === s.user_id}
                    onClick={() => onRemove(s.user_id, nameOf(s) === 'Unknown' ? 'this user' : nameOf(s))}
                    style={{ height: 32, padding: '0 12px', color: 'var(--g-danger)' }}>Remove</button>
                </span>
              )} />
          )}
        </>
      )}

      <Drawer
        open={open}
        title="Invite staff"
        onClose={() => setOpen(false)}
        footer={(
          <>
            <button className="btn outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn admin-primary" onClick={onInvite} disabled={saving}>{saving ? 'Sending...' : 'Send invite'}</button>
          </>
        )}
      >
        <p style={{ fontSize: 13, color: 'var(--g-text-2)', marginTop: 0 }}>
          They get admin access automatically the first time they sign in with this email (Google or Microsoft). No link is emailed.
        </p>
        <Field label="Work email">
          <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@aivons.com" />
        </Field>
        <Field label="Role">
          <select className="inp" value={role} onChange={(e) => setRole(e.target.value)}>
            {STAFF_ROLES.map((r) => (<option key={r} value={r}>{STAFF_ROLE_LABEL[r] || r}</option>))}
          </select>
        </Field>
        <Field label="Department" hint="Optional">
          <input className="inp" value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Engineering" />
        </Field>
      </Drawer>
    </div>
  );
}
